import { AdditionalFile } from '../entities/compliance/additional.file';
import { Attachment, File } from '../entities/shared/attachment';
import { ComplianceQuestionType } from '../entities/compliance/compliance.question.type';
import { JobMediaItem, JobMediaSource } from '../entities/job/job.media';
import { WorkComplianceAnswer } from '../entities/compliance/work.compliance.answer.types';
import * as Enumerable from 'linq';
import { Configuration } from '../configuration/api/configuration';

export class JobMediaUtility {

    public static convertToComplianceMedia(answers: WorkComplianceAnswer[], configuration: Configuration): JobMediaItem[] {

        let counter = 1;

        const items = Enumerable.from(answers)
            .selectMany(a => {

                const signature = JobMediaUtility.buildSignatureMedia(a, counter);
                const form = JobMediaUtility.buildFormMedia(a, configuration);
                const files = JobMediaUtility.buildMedia(a?.files, a, 'Files');
                const additionalFiles = JobMediaUtility.buildMedia(a?.attachments, a, 'Additional Files');

                const media = [
                    ...additionalFiles,
                    ...signature,
                    ...form,
                    ...files
                ] as JobMediaItem[];

                counter += 1;

                return JobMediaUtility.setWorkOrderNumber(media, a?.workOrderNumber);

            })
            .select(x => {

                x.source = JobMediaSource.Compliance;

                return x;
            })
            .toArray();

        return items;

    }

    public static convertToTaskMedia(answers: any[], configuration: Configuration): JobMediaItem[] {

        let counter = 1;

        const items = Enumerable.from(answers)
            .selectMany(a => {

                const signature = JobMediaUtility.buildSignatureMedia(a, counter);
                const form = JobMediaUtility.buildFormMedia(a, configuration);
                const files = JobMediaUtility.buildMedia(a?.files, a, 'Files');
                const beforePhotos = JobMediaUtility.buildMedia(a?.beforePhotos, a, ComplianceQuestionType.Before);
                const duringPhotos = JobMediaUtility.buildMedia(a?.duringPhotos, a, ComplianceQuestionType.During);
                const afterPhotos = JobMediaUtility.buildMedia(a?.afterPhotos, a, ComplianceQuestionType.After);
                const additionalFiles = JobMediaUtility.buildMedia(a?.attachments, a, 'Additional Files');

                const media = [
                    ...additionalFiles,
                    ...signature,
                    ...form,
                    ...files,
                    ...beforePhotos,
                    ...duringPhotos,
                    ...afterPhotos
                ] as JobMediaItem[];

                counter += 1;

                return media;

            })
            .select(x => {

                x.source = JobMediaSource.Task;

                return x;
            })
            .toArray();

        return items;

    }

    public static convertToActivityMedia(activities: JobMediaItem[]): JobMediaItem[] {

        const items = Enumerable.from(activities)
            .where(x => x?.type !== 'Form')
            .select((a: any) => {

                const uploadedBy = a?.updateBy?.name ?? a?.createdBy?.name;
                const uploadedDate = a?.updateDate ?? a?.createdAt;

                return {
                    ...a,
                    properties: [
                        { key: 'File name', value: a?.title },
                        { key: 'File type', value: a?.type },
                        { key: 'Uploaded by', value: uploadedBy ?? 'Not Set' },
                        { key: 'Uploaded At', value: uploadedDate ?? null },
                    ]

                }

            })
            .select(x => {
                x.source = JobMediaSource.Activity;
                return x;
            })
            .toArray();

        return items;

    }

    public static convertToFileMedia(files: File[]): JobMediaItem[] {


        return Enumerable.from(files).select(file => {

            const uploadedBy = file.createdBy?.name;
            const uploadedDate = file?.createdAt;

            const attachment = file.attachment;
            return {
                ...attachment,
                source: JobMediaSource.File,
                properties: [
                    { key: 'File name', value: attachment?.title },
                    { key: 'File type', value: attachment?.type },
                    { key: 'Uploaded by', value: uploadedBy ?? 'Not Set' },
                    { key: 'Uploaded At', value: uploadedDate ?? null },
                ]

            } as any;

        }).toArray();


    }

    public static convertToCodeMedia(answers: WorkComplianceAnswer[], configuration: Configuration): JobMediaItem[] {

        let counter = 1;

        const items = Enumerable.from(answers)
            .selectMany(a => {

                const beforePhotos = JobMediaUtility.buildMedia(a?.beforePhotos, a, ComplianceQuestionType.Before);
                const duringPhotos = JobMediaUtility.buildMedia(a?.duringPhotos, a, ComplianceQuestionType.During);
                const afterPhotos = JobMediaUtility.buildMedia(a?.afterPhotos, a, ComplianceQuestionType.After);
                const additionalFiles = JobMediaUtility.buildMedia(a?.attachments, a, 'Additional Files');
                const signature = JobMediaUtility.buildSignatureMedia(a, counter);
                const form = JobMediaUtility.buildFormMedia(a, configuration);
                const files = JobMediaUtility.buildMedia(a?.files, a, 'Files');

                const media = [
                    ...beforePhotos,
                    ...duringPhotos,
                    ...afterPhotos,
                    ...additionalFiles,
                    ...signature,
                    ...form,
                    ...files
                ];

                counter += 1;

                return media;

            })
            .select(x => {
                x.source = JobMediaSource.Code;
                return x;
            })
            .toArray();

        return items;

    }

    public static convertToAdditionalMedia(addtionalFiles: AdditionalFile[]): JobMediaItem[] {

        const attachments = Enumerable.from(addtionalFiles)
            .selectMany(x => x.attachments)
            .toArray();

        const items = Enumerable
            .from(attachments)
            .select(x => {

                const media = {
                    ...x,
                    properties: null
                } as any;

                media.properties = [
                    { key: 'File name', value: x?.title },
                    { key: 'Evidence Type', value: 'Additional File' },
                    { key: 'File type', value: x?.type },
                ];

                media.source = JobMediaSource.Code;

                return media;
            })
            .toArray();

        return items;

    }

    private static buildSignatureMedia(answer: WorkComplianceAnswer, counter: number): JobMediaItem[] {

        const signature = answer.signatureAnswered;

        if (!signature)
            return [];

        const media = {
            _id: `${answer?._id}::signature::${counter}`,
            type: 'Signature',
            title: `Signature`,
            path: signature,
            name: `Signature_${counter}`,
            fileName: `${answer?.workOrderNumber}_Signature_${counter}.jpg`,
            properties: null
        } as any;

        media.properties = JobMediaUtility.getMediaProperties(media, answer, 'Signature');

        return [media];
    }


    private static buildFormMedia(answer: WorkComplianceAnswer, configuration: Configuration): JobMediaItem[] {

        const formResponse = answer?.formResponse;

        if (!formResponse)
            return [];

        const urlEncodeForm = encodeURI(`https://${configuration.tenant}.${configuration.domain}/forms/${formResponse}`);
        const urlGeneratePdf = `/pdf?url=${urlEncodeForm}`;
        const data = `https://pdf.${configuration.domain}${urlGeneratePdf}`;


        const media = {
            _id: `${answer?._id}::form`,
            type: 'Form',
            title: answer.formTemplate?.name,
            path: formResponse,
            data: data,
            fileName: answer.formTemplate?.name,
            properties: null
        } as any;

        answer.type = 'Form' as any;

        media.properties = JobMediaUtility.getMediaProperties(media, answer, 'Form');

        return [media];


    }

    private static buildMedia(attachments: Attachment[], answer: WorkComplianceAnswer, type: string): JobMediaItem[] {

        if (!attachments?.length)
            return [];

        const listMedia = Enumerable.from(attachments)
            .select(x => {
                const media = {
                    ...x,
                    properties: null
                } as any;

                media.properties = JobMediaUtility.getMediaProperties(media, answer, type);

                return media;
            })
            .toArray();

        return listMedia;

    }

    public static setWorkOrderNumber(media: JobMediaItem[], workOrderNumber: string): JobMediaItem[] {

        if (!media?.length)
            return [];

        return Enumerable.from(media)
            .select(x => {

                x.workOrderNumber = workOrderNumber;

                return x;
            }).toArray()
    }


    private static getMediaProperties(attachment: Attachment, answer: WorkComplianceAnswer, type: string): any {

        const uploadedBy = answer?.updatedBy?.name || attachment.createdBy || 'Not Set';
        const uploadedAt = answer?.updatedAt || attachment.createdDate || null;

        return [
            { key: 'File name', value: attachment?.title },
            { key: 'Evidence Type', value: type },
            { key: 'File type', value: attachment?.type },
            { key: 'Uploaded by', value: uploadedBy },
            { key: 'Uploaded At', value: uploadedAt },
        ];

    }
}